import { tv } from "@ui/lib/tv";
import type { ReactNode, Ref } from "react";

const cardCarouselTrack = tv({
  base: [
    // Grid-flow-col (not flex) gives the row a definite height = tallest card; cards'
    // own `h-full` (e.g. `ListingCard.root`) then resolves and `mt-auto` on the price
    // row anchors to the bottom. Flex's auto cross-size leaves percentage heights
    // unresolved, so price rows misalign across cards.
    "scrollbar-hide grid grid-flow-col overflow-x-auto overflow-y-hidden",
    "snap-x snap-mandatory @3xl:snap-proximity",
    "auto-cols-[18rem] [&>*]:snap-start",
    // Bleed + matching padding: at scroll=0 the padding fills the bleed area so the
    // first card aligns with the page-padding inset; scrolling moves cards into the
    // bleed. `scroll-padding` matches `padding` so snap-mandatory doesn't auto-scroll
    // past the inset on first paint. Assumes PageFrame's `px-5 md:px-8`. Frame off at
    // `@3xl` where consumer-rendered arrow controls take over.
    "-mx-5 px-5 scroll-px-5",
    "md:-mx-8 md:px-8 md:scroll-px-8",
    "@3xl:mx-0 @3xl:px-0 @3xl:scroll-px-0",
  ],
  variants: {
    // The basis-step (auto-cols-[calc(...)]) classes live in the `gap` variants — not
    // in `compoundVariants` with `gap` — because Tailwind's class extractor reliably
    // picks them up here and missed them inside a compound. Don't refactor into
    // compounds without verifying the generated CSS includes these arbitrary values.
    gap: {
      md: [
        "gap-4 md:gap-6",
        "@3xl:auto-cols-[calc((100%-48px)/3)]",
        "@5xl:auto-cols-[calc((100%-72px)/4)]",
        "@7xl:auto-cols-[calc((100%-96px)/5)]",
      ],
      lg: [
        "gap-6 md:gap-8",
        "@3xl:auto-cols-[calc((100%-64px)/3)]",
        "@5xl:auto-cols-[calc((100%-96px)/4)]",
        "@7xl:auto-cols-[calc((100%-128px)/5)]",
      ],
      xl: [
        "gap-8 md:gap-10",
        "@3xl:auto-cols-[calc((100%-80px)/3)]",
        "@5xl:auto-cols-[calc((100%-120px)/4)]",
        "@7xl:auto-cols-[calc((100%-160px)/5)]",
      ],
    },
  },
  defaultVariants: { gap: "lg" },
});

export interface CardCarouselProps {
  children: ReactNode;
  /** Attach a ref from `useCardCarousel()` to wire scroll-position tracking and the
   *  paged-scroll callbacks for consumer-rendered arrow controls. Optional — carousels
   *  without arrows (e.g. mobile-only branches) can omit it. */
  ref?: Ref<HTMLDivElement>;
  /** Inter-card gap. Default `lg` (24px → 32px md+). */
  gap?: "md" | "lg" | "xl";
  className?: string;
}

/** Single-row horizontal scroll of cards. On narrow containers cards land at the 18rem
 *  target (`w-2xs`) — users swipe and a trailing cut-off card cues more content. On wide
 *  containers (≥ `@3xl` = 768px) cards size to exactly fill the row in steps of 3 / 4 / 5
 *  cards as the container grows, so there is no cut-off card next to the arrow controls.
 *  Bleeds past the surrounding page padding on narrow; frames cleanly at `@3xl` where
 *  arrow controls typically live.
 *
 *  For arrow controls, use `useCardCarousel()` to drive scroll-position state and
 *  paged-scroll callbacks, then render arrows wherever fits the surrounding layout. */
export function CardCarousel({ children, className, gap, ref }: CardCarouselProps) {
  return (
    <div className="@container">
      <div className={cardCarouselTrack({ gap, className })} ref={ref}>
        {children}
      </div>
    </div>
  );
}
